import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const schema = z.object({
  email: z.string().email("Invalid email address"),
});

type FormValues = z.infer<typeof schema>;

export interface InviteIndividualModalProps {
  open: boolean;
  saving: boolean;
  error: string | null;

  onOpenChange(open: boolean): void;
  onSubmit(email: string): any;
}

export function InviteIndividualModal({ open, saving, error, onOpenChange, onSubmit }: InviteIndividualModalProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const handleSubmit = async ({ email }: { email: string }) => {
    try {
      await onSubmit(email);
      form.reset();
    } catch (e: any) {
      toast.error(e.message ?? "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Invite New Individual</DialogTitle>
          <DialogDescription>
            Enter an individual e-mail. We'll send an invitation to join your team, or link them instantly if they're
            already on Shyne.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="email">Individual&nbsp;E-mail</Label>
            <Input id="email" {...form.register("email")} />
            {form.formState.errors.email && (
              <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>

          {error && <p className="text-sm text-destructive -mt-2">{error}</p>}

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Sending…" : "Send Invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
